/**
 * useEntitlements Hook
 *
 * Central hook for feature gating and plan limit enforcement.
 * Provides current plan info, limits, and helper functions.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useCurrentCompany } from '@/lib/api/companies';
import type {
  Entitlements,
  PlanSlug,
  SubscriptionStatus,
  BillingCycle,
  PlanFeatures,
  ResourceLimit,
} from '@shared/types';

// Default entitlements for unauthenticated or error states
const DEFAULT_ENTITLEMENTS: Entitlements = {
  planId: '',
  planSlug: 'free',
  planName: 'Free',
  status: 'active',
  isTrial: false,
  trialEndsAt: null,
  trialDaysRemaining: null,
  isGrandfathered: false,
  billingCycle: 'monthly',
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  limits: {
    users: { current: 0, max: 1, canAdd: false, percentUsed: 0 },
    contacts: { current: 0, max: 100, canAdd: true, percentUsed: 0 },
    deals: { current: 0, max: 10, canAdd: true, percentUsed: 0 },
    organizations: { current: 0, max: 10, canAdd: true, percentUsed: 0 },
    emailAccounts: { current: 0, max: 1, canAdd: true, percentUsed: 0 },
    workflows: { current: 0, max: 0, canAdd: false, percentUsed: 100 },
    storageGb: { current: 0, max: 1, canAdd: true, percentUsed: 0 },
    savedViews: { current: 0, max: 5, canAdd: true, percentUsed: 0 },
    projects: { current: 0, max: 3, canAdd: true, percentUsed: 0 },
    brandVoices: { current: 0, max: 1, canAdd: true, percentUsed: 0 },
  },
  features: {
    emailSync: false,
    calendarSync: false,
    workflows: false,
    aiFeatures: false,
    calling: false,
  },
  availableAddons: [],
};

/**
 * Fetch company's subscription and entitlements
 */
async function fetchEntitlements(companyId: string): Promise<Entitlements> {
  // Get subscription with plan details using RPC
  const { data: planData, error: planError } = await supabase.rpc('get_company_plan', {
    p_company_id: companyId,
  });

  if (planError) {
    console.error('Error fetching company plan:', planError);
    throw planError;
  }

  // If no subscription, return free plan defaults
  if (!planData || planData.length === 0) {
    return DEFAULT_ENTITLEMENTS;
  }

  const plan = planData[0];

  // Fetch current usage counts
  const [
    { count: userCount },
    { count: contactCount },
    { count: dealCount },
    { count: orgCount },
    { count: emailAccountCount },
    { count: workflowCount },
    { count: savedViewCount },
    { count: projectCount },
    { count: brandVoiceCount },
  ] = await Promise.all([
    supabase
      .from('company_members')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('deleted_at', null),
    supabase
      .from('contacts')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('deleted_at', null),
    supabase
      .from('deals')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('deleted_at', null),
    supabase
      .from('organizations')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('deleted_at', null),
    supabase
      .from('email_accounts')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('deleted_at', null),
    supabase
      .from('workflows')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('deleted_at', null),
    // Count organization views (combined across all object types)
    supabase
      .from('organization_views')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('deleted_at', null),
    // Count projects
    supabase
      .from('projects')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('deleted_at', null),
    // Count brand voices
    supabase
      .from('brand_voices')
      .select('*', { count: 'exact', head: true })
      .eq('company_id', companyId)
      .is('deleted_at', null),
  ]);

  // Fetch addon purchases
  const { data: addonPurchases } = await supabase
    .from('addon_purchases')
    .select('addon:addons(slug)')
    .eq('company_id', companyId)
    .eq('status', 'active')
    .is('deleted_at', null);

  // Calculate limits
  const createLimit = (current: number, max: number | null): ResourceLimit => ({
    current,
    max,
    canAdd: max === null || current < max,
    percentUsed: max === null ? 0 : Math.min(Math.round((current / max) * 100), 100),
  });

  // Calculate trial days remaining
  let trialDaysRemaining: number | null = null;
  if (plan.is_trial && plan.trial_end) {
    const now = new Date();
    const end = new Date(plan.trial_end);
    const diffMs = end.getTime() - now.getTime();
    trialDaysRemaining = diffMs > 0 ? Math.ceil(diffMs / (1000 * 60 * 60 * 24)) : 0;
  }

  // Combine included addons from plan + purchased addons
  const purchasedAddonSlugs = (addonPurchases || [])
    .map((p) => (p.addon as { slug: string })?.slug)
    .filter(Boolean);
  const availableAddons = [...(plan.included_addons || []), ...purchasedAddonSlugs];

  return {
    planId: plan.plan_id,
    planSlug: plan.plan_slug as PlanSlug,
    planName: plan.plan_name,
    status: plan.status as SubscriptionStatus,
    isTrial: plan.is_trial,
    trialEndsAt: plan.trial_end,
    trialDaysRemaining,
    isGrandfathered: plan.is_grandfathered,
    billingCycle: plan.billing_cycle as BillingCycle,
    currentPeriodEnd: plan.current_period_end,
    cancelAtPeriodEnd: false, // TODO: fetch from subscription
    limits: {
      users: createLimit(userCount || 0, plan.max_users),
      contacts: createLimit(contactCount || 0, plan.max_contacts),
      deals: createLimit(dealCount || 0, plan.max_deals),
      organizations: createLimit(orgCount || 0, plan.max_organizations),
      emailAccounts: createLimit(emailAccountCount || 0, plan.max_email_accounts),
      workflows: createLimit(workflowCount || 0, plan.max_workflows),
      storageGb: createLimit(0, plan.max_storage_gb), // TODO: calculate actual storage
      savedViews: createLimit(savedViewCount || 0, plan.max_saved_views),
      projects: createLimit(projectCount || 0, plan.max_projects),
      brandVoices: createLimit(brandVoiceCount || 0, plan.max_brand_voices),
    },
    features: plan.features as PlanFeatures,
    availableAddons,
  };
}

/**
 * Hook to get company's entitlements (plan, limits, features)
 */
export function useEntitlements() {
  const { data: company, isLoading: companyLoading } = useCurrentCompany();

  const {
    data: entitlements,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['entitlements', company?.id],
    queryFn: () => fetchEntitlements(company!.id),
    enabled: !!company?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes (renamed from cacheTime in v5)
  });

  return {
    entitlements: entitlements || DEFAULT_ENTITLEMENTS,
    isLoading: companyLoading || isLoading,
    error,
    refetch,
  };
}

/**
 * Hook to check if a specific feature is available
 */
export function useHasFeature(feature: keyof PlanFeatures): boolean {
  const { entitlements } = useEntitlements();
  return entitlements.features[feature] === true;
}

/**
 * Hook to check if an addon is available (included or purchased)
 */
export function useHasAddon(addonSlug: string): boolean {
  const { entitlements } = useEntitlements();
  return entitlements.availableAddons.includes(addonSlug);
}

/**
 * Hook to check if user can add more of a resource
 */
export function useCanAddResource(resource: keyof Entitlements['limits']): {
  canAdd: boolean;
  limit: ResourceLimit;
} {
  const { entitlements } = useEntitlements();
  const limit = entitlements.limits[resource];
  return {
    canAdd: limit.canAdd,
    limit,
  };
}

/**
 * Hook to check if subscription needs attention
 */
export function useNeedsAttention(): boolean {
  const { entitlements } = useEntitlements();
  return ['past_due', 'unpaid', 'canceled'].includes(entitlements.status);
}

/**
 * Hook to check if in trial and trial status
 */
export function useTrialStatus(): {
  isTrial: boolean;
  daysRemaining: number | null;
  isExpiringSoon: boolean;
} {
  const { entitlements } = useEntitlements();
  return {
    isTrial: entitlements.isTrial,
    daysRemaining: entitlements.trialDaysRemaining,
    isExpiringSoon:
      entitlements.isTrial &&
      entitlements.trialDaysRemaining !== null &&
      entitlements.trialDaysRemaining <= 7,
  };
}
